import { useState, useEffect, useCallback } from 'react'
import { gql } from 'graphql-tag'
import Server from '../server/VMSProServerAdapter'
import { DecisionFolderLite, DecisionLite } from '../types/decisionTypes'

export type UseDecision2Params = {
  decisionId: string
  accountId: string
}

export type UseDecision2Result = {
  ancestors: DecisionFolderLite[]
  decision: DecisionLite
}

export type UseDecision2Hook = {
  data: UseDecision2Result | null
  loading: boolean
  refetch: () => void
}

const DECISION_NODE2_QUERY = gql`
  query DecisionNode2($accountId: ID!, $decisionNodeId: ID!, $ancestors: Boolean!, $children: Boolean!) {
    decisionNode2(accountId: $accountId, decisionNodeId: $decisionNodeId, ancestors: $ancestors, children: $children) {
      decisionNode {
        id
        name
        entityType
        description {
          value
        }
        optionsCount
        policies {
          userId
          policyKey
        }
        decisionType
      }
      ancestors {
        id
        name
        entityType
        description {
          value
        }
        optionsCount
        policies {
          userId
          policyKey
        }
        decisionType
      }
      children {
        id
        name
        entityType
        description {
          value
        }
        optionsCount
        policies {
          userId
          policyKey
        }
        decisionType
      }
    }
  }
`

function transformToDecisionFolderLite(item: any): DecisionFolderLite {
  if (item.entityType !== 'DecisionFolder') {
    throw new Error(`Expected DecisionFolder, got ${item.entityType}`)
  }
  
  return {
    id: item.id,
    entityType: 'DecisionFolder',
    name: item.name,
    description: item.description,
    optionsCount: item.optionsCount,
    policies: item.policies,
    decisionType: null, // Always null for folders
  }
}

function transformToDecisionLite(item: any): DecisionLite {
  if (item.entityType !== 'Decision') {
    throw new Error(`Expected Decision, got ${item.entityType}`)
  }
  
  return {
    id: item.id,
    entityType: 'Decision',
    name: item.name,
    optionsCount: item.optionsCount,
    description: item.description,
    decisionType: item.decisionType,
  }
}

export function useDecision2({
  decisionId,
  accountId,
}: UseDecision2Params): UseDecision2Hook {
  const [data, setData] = useState<UseDecision2Result | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshKey, setRefreshKey] = useState(0)

  // Refetch function to trigger a refresh
  const refetch = useCallback(() => {
    setRefreshKey(prev => prev + 1)
  }, [])

  useEffect(() => {
    let cancelled = false

    const fetchData = async () => {
      setLoading(true)

      try {
        const response = await Server.graphql({
          query: DECISION_NODE2_QUERY,
          variables: {
            accountId,
            decisionNodeId: decisionId,
            ancestors: true,
            children: false,
          },
        })

        if (cancelled) return

        const { decisionNode, ancestors } = response.data.decisionNode2

        // Ensure the main node is a decision (throw error if not)
        if (decisionNode.entityType !== 'Decision') {
          throw new Error(`Expected Decision, but got ${decisionNode.entityType} with ID ${decisionId}`)
        }
        
        const decision = transformToDecisionLite(decisionNode)
        
        // Transform ancestors (should all be folders)
        const transformedAncestors = ancestors.map(transformToDecisionFolderLite)

        const result: UseDecision2Result = {
          ancestors: transformedAncestors,
          decision,
        }

        if (!cancelled) {
          setData(result)
        }
      } catch (error) {
        console.error('Error fetching decision data:', error)
        if (!cancelled) {
          setData(null)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchData()

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      cancelled = true
    }
  }, [decisionId, accountId, refreshKey])

  return {
    data,
    loading,
    refetch,
  }
}